import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'

import { ProfileAlert } from '@modules/alerts/types'
import useContactWithProfilesCounts from '@modules/contacts/hooks/useContactWithProfilesCounts'
import ProfileHeader from '@modules/profile/ProfileHeader'
import { DEFAULT_COMPANY_LOGO_SRC } from '@modules/profile/constants'
import { COMPANY_NON_EMPTY_STATE_FIELDS } from '@modules/profile/profileConfig'
import { Company, CompanyHistoryProfile, ProfileType } from '@modules/search/types'

import ProfileDetails from '../ProfileDetails'
import ProfileEvents from '../ProfileEvents'
import {
  ProfileContent,
  ProfileContentWrapper,
  ProfileEmptySection,
  ProfileEmptySectionMessage,
  ProfileLogoWrapper,
  ProfileMainColumn,
  ProfileOtherInfo,
  ProfileOuterWrapper,
  ProfileSkeleton,
  ProfileTypeLogo,
  ProfileWrapper,
  ProfileWrapperInner,
} from '../index.styles'

interface CompanyProfileProps {
  profile?: CompanyHistoryProfile
  isError?: boolean
  isFetching?: boolean
  profileAlert?: ProfileAlert
  isAllDelegatesPageEnabled?: boolean
}

const CompanyProfile = (props: CompanyProfileProps) => {
  const { profile, isError, isFetching, profileAlert, isAllDelegatesPageEnabled = true } = props
  const { id: companyId, latest: companyData, events } = profile || ({} as CompanyHistoryProfile)
  const { t } = useTranslation()

  const countsByCompanyId = useContactWithProfilesCounts({ companyIds: [companyId] })
  const isPrivate = useMemo(() => companyData?.isPrivate, [companyData])
  const isProfileEmpty = useMemo(() => {
    if (!companyData || !companyData.isPublished) {
      return true
    }

    if (countsByCompanyId[companyId]) {
      return false
    }

    return COMPANY_NON_EMPTY_STATE_FIELDS.every(f => {
      const field = f as keyof Company

      if (Array.isArray(companyData[field])) {
        const values = companyData?.[field] as string[]

        return !values?.length
      }

      return !companyData[field]
    })
  }, [companyData, countsByCompanyId, companyId])

  return (
    <ProfileOuterWrapper>
      <ProfileWrapper>
        <ProfileWrapperInner>
          <ProfileContentWrapper>
            {isError && t(translationKeys.FETCHING_ERROR)}
            {isFetching && (
              <ProfileContent>
                <ProfileMainColumn>
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                </ProfileMainColumn>
                <ProfileOtherInfo>
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                </ProfileOtherInfo>
                <ProfileOtherInfo>
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                  <ProfileSkeleton animation="wave" />
                </ProfileOtherInfo>
              </ProfileContent>
            )}
            {companyData && (
              <>
                <ProfileHeader
                  isAllDelegatesPageEnabled={isAllDelegatesPageEnabled}
                  profile={profile as CompanyHistoryProfile}
                  profileName={{ title: companyData.name }}
                  isPrivate={isPrivate}
                  updatedAt={companyData?.updatedAt}
                  companyId={companyData?.id}
                  profileType={ProfileType.company}
                  profileAlert={profileAlert}
                />
                <ProfileContent>
                  {isProfileEmpty ? (
                    <ProfileEmptySection>
                      <ProfileEmptySectionMessage variant="h3" data-testid="profile-empty-section">
                        <ProfileLogoWrapper emptySection>
                          <ProfileTypeLogo component="img" src={`${DEFAULT_COMPANY_LOGO_SRC}`} />
                        </ProfileLogoWrapper>
                        {t(translationKeys.PROFILE_SECTION_EMPTY)}
                      </ProfileEmptySectionMessage>
                    </ProfileEmptySection>
                  ) : (
                    <>
                      <ProfileMainColumn>
                        <ProfileDetails
                          profileData={companyData}
                          profileType={ProfileType.company}
                          profileAreaName="mainInfo"
                          profileId={companyId}
                          profileAlert={profileAlert}
                        />
                      </ProfileMainColumn>
                      <ProfileOtherInfo>
                        {!!events?.length && (
                          <ProfileEvents events={events} name={companyData.name} lastViewedAt={profileAlert?.lastViewedAt} />
                        )}
                        <ProfileDetails
                          profileData={companyData}
                          profileType={ProfileType.company}
                          profileAreaName="secondColumnInfo"
                          profileAlert={profileAlert}
                        />
                      </ProfileOtherInfo>
                      <ProfileOtherInfo>
                        <ProfileDetails
                          profileData={companyData}
                          profileType={ProfileType.company}
                          profileAreaName="thirdColumnInfo"
                          profileAlert={profileAlert}
                        />
                      </ProfileOtherInfo>
                    </>
                  )}
                </ProfileContent>
              </>
            )}
          </ProfileContentWrapper>
        </ProfileWrapperInner>
      </ProfileWrapper>
    </ProfileOuterWrapper>
  )
}

export default CompanyProfile
